import { JSX, useCallback, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { Accordion } from 'Components/Accordion/Accordion';
import { AccordionCollapse } from 'Components/Accordion/AccordionCollapse/AccordionCollapse';
import { Button } from 'Components/Buttons/Buttons';
import { OverflowMenu } from 'Components/Buttons/OverflowMenu';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { CheckedStatus } from 'Components/Checkbox/CheckboxWithoutLabel';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FixedFooter } from 'Components/FixedFooter/FixedFooter';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE, ICON_DELETE_REMOVE, ICON_DOWNLOAD, ICON_EDIT_MODIFY_UPDATE, ICON_SAVE } from 'Config/Icons';
import { SETTINGS, TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION, TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING, UCRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING } from 'Config/PathTemplates';
import { TPRM_ASSESSMENT_CONFIGURATION_DELEGATES, TPRM_ASSESSMENT_CONFIGURATION_OWNER, UCRM_ASSESSMENT_CONFIGURATION_DELEGATES, UCRM_ASSESSMENT_CONFIGURATION_OWNER } from 'Config/Tooltips';
import { RequestState } from 'Helpers/RequestUtils';
import { getDueDiligenceModuleRoles } from 'Helpers/UserUtils';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { DueDiligenceAssessmentConfiguration, DueDiligenceControlConfiguration, DueDiligenceControlFrameworkConfiguration, DueDiligenceControlGroupConfiguration, DueDiligenceQuestionConfiguration, DueDiligenceQuestionType, UpdateDueDiligenceAssessmentConfigurationRequest } from 'Models/DueDiligence';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating, riskRatingAsString } from 'Models/RiskRating';
import { UserResponse } from 'Models/User';

import styles from './DueDiligenceAssessmentSettings.module.css';
import { DueDiligenceAssessmentCheckboxRow, RiskRatingCheckedStatuses } from '../Checkboxes/DueDiligenceAssessmentCheckboxRow';
import { ConfigurationRow } from '../ConfigurationRow/ConfigurationRow';
import { ControlFrameworkModal } from '../Modals/ControlFrameworkModal';
import { ControlGroupModal } from '../Modals/ControlGroupModal';
import { ControlModal } from '../Modals/ControlModal';
import { QuestionModal } from '../Modals/QuestionModal';

export interface DueDiligenceAssessmentSettingsLoaderProps {
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    module: DueDiligenceModule;
}

export const DueDiligenceAssessmentSettingsLoader = (props: DueDiligenceAssessmentSettingsLoaderProps): JSX.Element => {
    return <PageDataLoader path={TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION} fetchData={(urlParams) => props.dueDiligenceAssessmentConfigurationApi.getAssessmentConfiguration(urlParams.assessmentId)} renderForm={(result) => <DueDiligenceAssessmentSettings initialConfiguration={result.data} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} module={props.module} />} />;
};

type DisplayedModal =
    | {
          type: 'addFramework';
      }
    | {
          type: 'editFramework';
          framework: DueDiligenceControlFrameworkConfiguration;
      }
    | {
          type: 'deleteFramework';
          framework: DueDiligenceControlFrameworkConfiguration;
      }
    | {
          type: 'addGroup';
          framework: DueDiligenceControlFrameworkConfiguration;
      }
    | {
          type: 'editGroup';
          framework: DueDiligenceControlFrameworkConfiguration;
          group: DueDiligenceControlGroupConfiguration;
      }
    | {
          type: 'deleteGroup';
          framework: DueDiligenceControlFrameworkConfiguration;
          group: DueDiligenceControlGroupConfiguration;
      }
    | {
          type: 'addControl';
          framework: DueDiligenceControlFrameworkConfiguration;
          group: DueDiligenceControlGroupConfiguration;
      }
    | {
          type: 'editControl';
          framework: DueDiligenceControlFrameworkConfiguration;
          group: DueDiligenceControlGroupConfiguration;
          control: DueDiligenceControlConfiguration;
      }
    | {
          type: 'deleteControl';
          framework: DueDiligenceControlFrameworkConfiguration;
          group: DueDiligenceControlGroupConfiguration;
          control: DueDiligenceControlConfiguration;
      }
    | {
          type: 'addQuestion';
          framework: DueDiligenceControlFrameworkConfiguration;
          group: DueDiligenceControlGroupConfiguration;
          control: DueDiligenceControlConfiguration;
      }
    | {
          type: 'editQuestion';
          framework: DueDiligenceControlFrameworkConfiguration;
          group: DueDiligenceControlGroupConfiguration;
          control: DueDiligenceControlConfiguration;
          question: DueDiligenceQuestionConfiguration;
      }
    | {
          type: 'deleteQuestion';
          framework: DueDiligenceControlFrameworkConfiguration;
          group: DueDiligenceControlGroupConfiguration;
          control: DueDiligenceControlConfiguration;
          question: DueDiligenceQuestionConfiguration;
      };

interface DueDiligenceAssessmentSettingsProps {
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    initialConfiguration: DueDiligenceAssessmentConfiguration;
    module: DueDiligenceModule;
}

const DueDiligenceAssessmentSettings = (props: DueDiligenceAssessmentSettingsProps): JSX.Element => {
    const cachedData = useCachedData();

    // Normalize the order of each question's mapped risk ratings and of the default delegate IDs on load, since the backend returns both in an arbitrary order (due to being stored as sets). This keeps the unsaved-changes baseline consistent with the canonical order produced as the user edits the form (see `normalizeRiskRatingOrder`). Sorting the delegate IDs has no visual effect, because the delegates are displayed in the order of the cached users rather than the order of this array.
    const [configuration, setConfiguration] = useState<DueDiligenceAssessmentConfiguration>(() => normalizeRiskRatingOrder({ ...props.initialConfiguration, default_delegate_ids: [...props.initialConfiguration.default_delegate_ids].sort() }));
    const [hasUnsavedChanges, onConfigurationSaved] = useUnsavedChangesTracking(configuration, false);

    // Track expanded rows so that we have full control over expand+collapse. By default, expand everything.
    const [expandedRows, setExpandedRows] = useState<Set<string>>(() => {
        const keys = new Set<string>();
        for (const framework of configuration.control_frameworks) {
            keys.add(framework.name);
            for (const group of framework.control_groups) {
                const groupKey = `${framework.name}#${group.name}`;
                keys.add(groupKey);
                for (const control of group.controls) {
                    const controlKey = `${framework.name}#${group.name}#${control.name}`;
                    keys.add(controlKey);
                }
            }
        }
        return keys;
    });
    const [displayedModal, setDisplayedModal] = useState<DisplayedModal>();
    const [requestState, setRequestState] = useState<RequestState>({ type: 'idle' });

    /** Called when the user clicks the expand/collapse icon next to a row. */
    const toggleRowIsExpanded = (eventKey: string): void => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(eventKey)) {
                next.delete(eventKey);
            } else {
                next.add(eventKey);
            }
            return next;
        });
    };

    /** Called when a new row is added or an existing row is renamed, so that the row expands or remains expanded. */
    const expandRow = (eventKey: string): void => {
        setExpandedRows((prev) => new Set(prev).add(eventKey));
    };

    /** Updates all keys in expandedKeys that start with oldPrefix to use newPrefix instead. */
    const updateExpandedKeysPrefix = (oldPrefix: string, newPrefix: string): void => {
        setExpandedRows((prev) => {
            const next = new Set<string>();
            for (const key of prev) {
                if (key === oldPrefix) {
                    next.add(newPrefix);
                } else if (key.startsWith(oldPrefix + '#')) {
                    next.add(newPrefix + key.substring(oldPrefix.length));
                } else {
                    next.add(key);
                }
            }
            return next;
        });
    };

    const handleSave = async (): Promise<void> => {
        setRequestState({ type: 'inProgress' });

        try {
            const request: UpdateDueDiligenceAssessmentConfigurationRequest = { configuration: configuration };
            await props.dueDiligenceAssessmentConfigurationApi.updateAssessmentConfiguration(configuration.id, request);
            setRequestState({ type: 'success', message: 'Configuration saved.' }); // Note: This is message is never actually displayed. The lack of an unsaved-changes warning (or failure message) in the footer indicates success.
            onConfigurationSaved(configuration);
        } catch (error) {
            console.error(error.message);
            setRequestState({ type: 'failure', message: error.message });
        }
    };

    /**
     * Download the assessment configuration as a CSV file, with one row per question.
     * This exports the in-memory configuration, so unsaved changes are included.
     */
    const downloadCsv = (): void => {
        const escapeCsvField = (field: string) => {
            if (field.includes(',') || field.includes('"') || field.includes('\n')) {
                return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
        };

        const rows: string[][] = [['Control Framework', 'Control Group', 'Control', 'Question', 'Question Type', 'Required', 'Possible Answers', riskRatingAsString(RiskRating.LOW), riskRatingAsString(RiskRating.LOW_MODERATE), riskRatingAsString(RiskRating.MODERATE), riskRatingAsString(RiskRating.MODERATE_HIGH), riskRatingAsString(RiskRating.HIGH)]];

        configuration.control_frameworks.forEach((framework) => {
            framework.control_groups.forEach((group) => {
                group.controls.forEach((control) => {
                    control.questions.forEach((question) => {
                        const possibleAnswers: string = ((): string => {
                            switch (question._type) {
                                case DueDiligenceQuestionType.DATE:
                                case DueDiligenceQuestionType.FILE_UPLOAD:
                                case DueDiligenceQuestionType.FREEFORM_TEXT:
                                    return '';
                                case DueDiligenceQuestionType.MULTIPLE_SELECT:
                                case DueDiligenceQuestionType.SINGLE_SELECT:
                                    // Whether an answer is unacceptable is not included, since that information is not intended to be seen by third-party users.
                                    return question.options.map((option) => option.text).join(', ');
                            }
                        })();

                        const getIsMappedToString = (riskRating: RiskRating): string => (question.mapped_risk_ratings.includes(riskRating) ? 'Yes' : 'No');
                        rows.push([framework.name, group.name, control.name, question.text, question._type, question.is_required ? 'Yes' : 'No', possibleAnswers, getIsMappedToString(RiskRating.LOW), getIsMappedToString(RiskRating.LOW_MODERATE), getIsMappedToString(RiskRating.MODERATE), getIsMappedToString(RiskRating.MODERATE_HIGH), getIsMappedToString(RiskRating.HIGH)]);
                    });
                });
            });
        });

        const csvContent = rows.map((row) => row.map(escapeCsvField).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${configuration.name.replace(/[/\\:*?"<>|]/g, '_')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const hideModal = () => setDisplayedModal(undefined);

    const handleQuestionCheckboxChange = (frameworkName: string, groupName: string, controlName: string, questionText: string, checked: boolean, riskRating: RiskRating): void => {
        setConfiguration((prev) => {
            return {
                ...prev,
                control_frameworks: prev.control_frameworks.map((framework) =>
                    framework.name === frameworkName
                        ? {
                              ...framework,
                              control_groups: framework.control_groups.map((group) =>
                                  group.name === groupName
                                      ? {
                                            ...group,
                                            controls: group.controls.map((control) => (control.name === controlName ? { ...control, questions: control.questions.map((question) => (question.text === questionText ? updateQuestionRiskRatings(question, checked, riskRating) : question)) } : control)),
                                        }
                                      : group
                              ),
                          }
                        : framework
                ),
            };
        });
    };

    const handleControlCheckboxChange = (frameworkName: string, groupName: string, controlName: string, checked: boolean, riskRating: RiskRating): void => {
        setConfiguration((prev) => {
            return {
                ...prev,
                control_frameworks: prev.control_frameworks.map((framework) =>
                    framework.name === frameworkName
                        ? {
                              ...framework,
                              control_groups: framework.control_groups.map((group) =>
                                  group.name === groupName
                                      ? {
                                            ...group,
                                            controls: group.controls.map((control) => (control.name === controlName ? { ...control, questions: control.questions.map((question) => updateQuestionRiskRatings(question, checked, riskRating)) } : control)),
                                        }
                                      : group
                              ),
                          }
                        : framework
                ),
            };
        });
    };

    const handleGroupCheckboxChange = (frameworkName: string, groupName: string, checked: boolean, riskRating: RiskRating): void => {
        setConfiguration((prev) => {
            return {
                ...prev,
                control_frameworks: prev.control_frameworks.map((framework) =>
                    framework.name === frameworkName
                        ? {
                              ...framework,
                              control_groups: framework.control_groups.map((group) =>
                                  group.name === groupName
                                      ? {
                                            ...group,
                                            controls: group.controls.map((control) => ({
                                                ...control,
                                                questions: control.questions.map((question) => updateQuestionRiskRatings(question, checked, riskRating)),
                                            })),
                                        }
                                      : group
                              ),
                          }
                        : framework
                ),
            };
        });
    };

    const handleFrameworkCheckboxChange = (frameworkName: string, checked: boolean, riskRating: RiskRating): void => {
        setConfiguration((prev) => {
            return {
                ...prev,
                control_frameworks: prev.control_frameworks.map((framework) =>
                    framework.name === frameworkName
                        ? {
                              ...framework,
                              control_groups: framework.control_groups.map((group) => ({
                                  ...group,
                                  controls: group.controls.map((control) => ({
                                      ...control,
                                      questions: control.questions.map((question) => updateQuestionRiskRatings(question, checked, riskRating)),
                                  })),
                              })),
                          }
                        : framework
                ),
            };
        });
    };

    const renderAccordionHeader = (): JSX.Element => (
        <div className={styles.accordionHeaderContainer}>
            <div className={styles.accordionRow}>
                <Text noStyles>Framework / Group / Control / Question</Text>
                <div className={styles.accordionRightContent}>
                    <Text noStyles>{riskRatingAsString(RiskRating.LOW)}</Text>
                    <Text noStyles>{riskRatingAsString(RiskRating.LOW_MODERATE)}</Text>
                    <Text noStyles>{riskRatingAsString(RiskRating.MODERATE)}</Text>
                    <Text noStyles>{riskRatingAsString(RiskRating.MODERATE_HIGH)}</Text>
                    <Text noStyles>{riskRatingAsString(RiskRating.HIGH)}</Text>
                    <div className={styles.overflowMenuSpacer} />
                </div>
            </div>
        </div>
    );

    const renderControl = useCallback(
        (control: DueDiligenceControlConfiguration, group: DueDiligenceControlGroupConfiguration, framework: DueDiligenceControlFrameworkConfiguration): JSX.Element => {
            const renderQuestion = (question: DueDiligenceQuestionConfiguration, control: DueDiligenceControlConfiguration, group: DueDiligenceControlGroupConfiguration, framework: DueDiligenceControlFrameworkConfiguration): JSX.Element => (
                <ConfigurationRow key={question.text} level={3}>
                    <div className={styles.accordionRow}>
                        <div className={styles.question}>
                            <Text noStyles>{question.text}</Text>
                            <Text noStyles color="darkGray">
                                {question._type} {question.is_required ? <span className={styles.requiredOrUnacceptable}>(required)</span> : ''}
                            </Text>
                            {((): JSX.Element => {
                                switch (question._type) {
                                    case DueDiligenceQuestionType.DATE:
                                    case DueDiligenceQuestionType.FILE_UPLOAD:
                                    case DueDiligenceQuestionType.FREEFORM_TEXT:
                                        return <></>;
                                    case DueDiligenceQuestionType.MULTIPLE_SELECT:
                                    case DueDiligenceQuestionType.SINGLE_SELECT:
                                        return (
                                            <Text noStyles color="darkGray">
                                                Options:{' '}
                                                {question.options.map((option, index) => (
                                                    <span key={option.text}>
                                                        {index > 0 ? ', ' : ''}
                                                        {option.text}
                                                        {option.is_unacceptable ? <span className={styles.requiredOrUnacceptable}> (unacceptable)</span> : ''}
                                                    </span>
                                                ))}
                                            </Text>
                                        );
                                }
                            })()}
                        </div>
                        <div className={styles.accordionRightContent}>
                            <DueDiligenceAssessmentCheckboxRow {...questionToCheckboxes(question)} onToggled={(checked, rating) => handleQuestionCheckboxChange(framework.name, group.name, control.name, question.text, checked, rating)} />
                            <OverflowMenu
                                overflowItems={[
                                    { text: 'Edit Question', icon: ICON_EDIT_MODIFY_UPDATE, onClickAction: () => setDisplayedModal({ type: 'editQuestion', framework, group, control, question }) },
                                    { text: 'Delete Question', icon: ICON_DELETE_REMOVE, onClickAction: () => setDisplayedModal({ type: 'deleteQuestion', framework, group, control, question }) },
                                ]}
                                accessibilityTitle={`Menu for question: ${question.text}`}
                            />
                        </div>
                    </div>
                </ConfigurationRow>
            );

            const controlKey = `${framework.name}#${group.name}#${control.name}`;
            return (
                <Accordion key={controlKey} activeKey={expandedRows.has(controlKey) ? controlKey : undefined} onSelect={() => toggleRowIsExpanded(controlKey)}>
                    <ConfigurationRow level={2} eventKey={controlKey} accessibilityTitle={control.name}>
                        <div className={styles.accordionRow}>
                            <div className={styles.control}>
                                <Text noStyles>{control.name}</Text>
                                {control.text && (
                                    <Text noStyles color="darkGray">
                                        {control.text}
                                    </Text>
                                )}
                            </div>
                            <div className={styles.accordionRightContent}>
                                {controlHasQuestions(control) ? <DueDiligenceAssessmentCheckboxRow {...getControlCheckboxes(control)} onToggled={(checked, rating) => handleControlCheckboxChange(framework.name, group.name, control.name, checked, rating)} /> : <div className={styles.checkboxSpacer} />}
                                <OverflowMenu
                                    overflowItems={[
                                        { text: 'Edit Control', icon: ICON_EDIT_MODIFY_UPDATE, onClickAction: () => setDisplayedModal({ type: 'editControl', framework, group, control }) },
                                        { text: 'Add Question', icon: ICON_ADD_CREATE, onClickAction: () => setDisplayedModal({ type: 'addQuestion', framework, group, control }) },
                                        { text: 'Delete Control', icon: ICON_DELETE_REMOVE, onClickAction: () => setDisplayedModal({ type: 'deleteControl', framework, group, control }) },
                                    ]}
                                    accessibilityTitle={`Menu for control: ${control.name}`}
                                />
                            </div>
                        </div>
                    </ConfigurationRow>
                    <AccordionCollapse eventKey={controlKey}>
                        <>{control.questions.map((question) => renderQuestion(question, control, group, framework))}</>
                    </AccordionCollapse>
                </Accordion>
            );
        },
        [expandedRows]
    );

    const renderControlGroup = useCallback(
        (group: DueDiligenceControlGroupConfiguration, framework: DueDiligenceControlFrameworkConfiguration): JSX.Element => {
            const groupKey = `${framework.name}#${group.name}`;
            return (
                <Accordion key={groupKey} activeKey={expandedRows.has(groupKey) ? groupKey : undefined} onSelect={() => toggleRowIsExpanded(groupKey)}>
                    <ConfigurationRow level={1} eventKey={groupKey} accessibilityTitle={group.name}>
                        <div className={styles.accordionRow}>
                            <div className={styles.control}>
                                <Text noStyles>{group.name}</Text>
                                {group.description && (
                                    <Text noStyles color="darkGray">
                                        {group.description}
                                    </Text>
                                )}
                            </div>
                            <div className={styles.accordionRightContent}>
                                {groupHasQuestions(group) ? <DueDiligenceAssessmentCheckboxRow {...getControlGroupCheckboxes(group)} onToggled={(checked, rating) => handleGroupCheckboxChange(framework.name, group.name, checked, rating)} /> : <div className={styles.checkboxSpacer} />}
                                <OverflowMenu
                                    overflowItems={[
                                        { text: 'Edit Control Group', icon: ICON_EDIT_MODIFY_UPDATE, onClickAction: () => setDisplayedModal({ type: 'editGroup', framework, group }) },
                                        { text: 'Add Control', icon: ICON_ADD_CREATE, onClickAction: () => setDisplayedModal({ type: 'addControl', framework, group }) },
                                        { text: 'Delete Control Group', icon: ICON_DELETE_REMOVE, onClickAction: () => setDisplayedModal({ type: 'deleteGroup', framework, group }) },
                                    ]}
                                    accessibilityTitle={`Menu for control group: ${group.name}`}
                                />
                            </div>
                        </div>
                    </ConfigurationRow>
                    <AccordionCollapse eventKey={groupKey}>
                        <>{group.controls.map((control) => renderControl(control, group, framework))}</>
                    </AccordionCollapse>
                </Accordion>
            );
        },
        [expandedRows, renderControl]
    );

    const renderFramework = useCallback(
        (framework: DueDiligenceControlFrameworkConfiguration): JSX.Element => (
            <Accordion key={framework.name} activeKey={expandedRows.has(framework.name) ? framework.name : undefined} onSelect={() => toggleRowIsExpanded(framework.name)}>
                <ConfigurationRow level={0} eventKey={framework.name} accessibilityTitle={framework.name}>
                    <div className={styles.accordionRow}>
                        <div className={styles.control}>
                            <Text noStyles>{framework.name}</Text>
                        </div>
                        <div className={styles.accordionRightContent}>
                            {frameworkHasQuestions(framework) ? <DueDiligenceAssessmentCheckboxRow {...getFrameworkCheckboxes(framework)} onToggled={(checked, rating) => handleFrameworkCheckboxChange(framework.name, checked, rating)} /> : <div className={styles.checkboxSpacer} />}
                            <OverflowMenu
                                overflowItems={[
                                    { text: 'Edit Control Framework', icon: ICON_EDIT_MODIFY_UPDATE, onClickAction: () => setDisplayedModal({ type: 'editFramework', framework }) },
                                    { text: 'Add Control Group', icon: ICON_ADD_CREATE, onClickAction: () => setDisplayedModal({ type: 'addGroup', framework }) },
                                    { text: 'Delete Control Framework', icon: ICON_DELETE_REMOVE, onClickAction: () => setDisplayedModal({ type: 'deleteFramework', framework }) },
                                ]}
                                accessibilityTitle={`Menu for control framework: ${framework.name}`}
                            />
                        </div>
                    </div>
                </ConfigurationRow>
                <AccordionCollapse eventKey={framework.name}>
                    <>{framework.control_groups.map((group) => renderControlGroup(group, framework))}</>
                </AccordionCollapse>
            </Accordion>
        ),
        [expandedRows, renderControlGroup]
    );

    const getExistingGroupNames = (framework: DueDiligenceControlFrameworkConfiguration, excludeName?: string): string[] => {
        const names = framework.control_groups.map((g) => g.name) ?? [];
        return excludeName ? names.filter((n) => n !== excludeName) : names;
    };

    const getExistingControlNames = (group: DueDiligenceControlGroupConfiguration, excludeName?: string): string[] => {
        const names = group?.controls.map((c) => c.name) ?? [];
        return excludeName ? names.filter((n) => n !== excludeName) : names;
    };

    const getExistingQuestionTexts = (control: DueDiligenceControlConfiguration, excludeText?: string): string[] => {
        const texts = control?.questions.map((q) => q.text) ?? [];
        return excludeText ? texts.filter((t) => t !== excludeText) : texts;
    };

    /**
     * The accordion of frameworks/groups/controls/questions. This tree is expensive to build for a lengthy assessment, so it is memoized to avoid rebuilding it on every render.
     * Without this memoization, editing the assessment's name, default owner, default delegates, or default flag feels extremely sluggish.
     */
    const frameworksContent = useMemo(
        (): JSX.Element => (
            <>
                {renderAccordionHeader()}
                {configuration.control_frameworks.map(renderFramework)}
            </>
        ),
        [configuration.control_frameworks, renderFramework]
    );

    if (!configuration) {
        return <Placeholder />;
    }

    return (
        <div className={styles.contentWithPaddingForFooter}>
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(SETTINGS)}>Settings</BreadcrumbLink>
                        <BreadcrumbLink link={generatePath(props.module === ProductModule.UCRM ? UCRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING : TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING)}>{props.module === ProductModule.UCRM ? 'Use Case Due Diligence Assessments Configuration' : 'Third-Party Due Diligence Assessments Configuration'}</BreadcrumbLink>
                        <BreadcrumbText>{configuration.name}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={configuration.name}
                headerDescription={`Configure the control frameworks, control groups, controls, and questions for this assessment. The ${props.module === ProductModule.UCRM ? 'respondent' : 'third party'} cannot submit a questionnaire until all its required questions are answered. If a question has an answer option that is unacceptable, the question must be required. When the ${props.module === ProductModule.UCRM ? 'respondent' : 'third party'} submits a questionnaire, a draft issue will be created automatically for each unacceptable answer submitted.`}
                headerButtons={
                    <>
                        <Button variant="secondary" onClick={downloadCsv} fontAwesomeImage={ICON_DOWNLOAD}>
                            Download
                        </Button>
                        <Button variant="secondary" onClick={() => setDisplayedModal({ type: 'addFramework' })} fontAwesomeImage={ICON_ADD_CREATE}>
                            Add Control Framework
                        </Button>
                    </>
                }
                body={[
                    {
                        title: 'Assessment Settings',
                        content: (
                            <>
                                <div className={styles.formFieldRow}>
                                    <div className={styles.formField}>
                                        <FormFieldText
                                            formFieldId="assessmentName"
                                            formFieldLabel="Name"
                                            value={configuration.name}
                                            handleChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                                                setConfiguration({ ...configuration, name: event.target.value });
                                            }}
                                            required
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        <FormFieldUserSelect
                                            notClearable
                                            rolesRestriction={getDueDiligenceModuleRoles(props.module)}
                                            selectedUser={cachedData.users.find((user) => user.cognito_subject === configuration.default_owner_id)}
                                            formFieldId="defaultOwner"
                                            formFieldLabel="Default Owner"
                                            onUserSelected={(user?: UserResponse) => {
                                                if (user) {
                                                    setConfiguration({ ...configuration, default_owner_id: user.cognito_subject });
                                                }
                                            }}
                                            required
                                            tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_CONFIGURATION_OWNER : TPRM_ASSESSMENT_CONFIGURATION_OWNER}
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        <FormFieldUserMultiSelect
                                            rolesRestriction={getDueDiligenceModuleRoles(props.module)}
                                            selectedUsers={cachedData.users.filter((user) => configuration.default_delegate_ids.includes(user.cognito_subject))}
                                            formFieldId="defaultDelegates"
                                            formFieldLabel="Default Delegates"
                                            onUsersSelected={(users?: UserResponse[]) => {
                                                setConfiguration({ ...configuration, default_delegate_ids: (users ?? []).map((user) => user.cognito_subject) });
                                            }}
                                            tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_CONFIGURATION_DELEGATES : TPRM_ASSESSMENT_CONFIGURATION_DELEGATES}
                                        />
                                    </div>
                                </div>
                                <Checkbox
                                    checked={configuration.is_default}
                                    onToggled={(isChecked: boolean): void => {
                                        setConfiguration({ ...configuration, is_default: isChecked });
                                    }}
                                    label="Is default assessment"
                                    tooltip={`When an inherent risk questionnaire is submitted, the assessments applicable to the ${props.module === ProductModule.UCRM ? 'use case' : 'third-party service'} must be selected. Default assessments will be selected by default.`}
                                />
                                {frameworksContent}
                            </>
                        ),
                    },
                ]}
            />

            <FixedFooter
                hasUnsavedChanges={hasUnsavedChanges}
                errorMessage={requestState?.type === 'failure' ? requestState.message : undefined}
                content={
                    <Text variant="Text2" noStyles>
                        {configuration.name}
                    </Text>
                }
                buttons={
                    <Button variant="primary" isLoading={requestState?.type === 'inProgress'} fontAwesomeImage={ICON_SAVE} loadingText="Saving..." onClick={handleSave} disabled={requestState?.type === 'inProgress'}>
                        Save
                    </Button>
                }
            />

            {displayedModal?.type === 'addFramework' && (
                <ControlFrameworkModal
                    hideModal={hideModal}
                    existingNames={configuration.control_frameworks.map((f) => f.name)}
                    onSave={(newFramework: DueDiligenceControlFrameworkConfiguration): void => {
                        setConfiguration((prev) => {
                            return { ...prev, control_frameworks: [...prev.control_frameworks, newFramework] };
                        });
                        expandRow(newFramework.name);
                        hideModal();
                    }}
                />
            )}
            {displayedModal?.type === 'editFramework' && (
                <ControlFrameworkModal
                    hideModal={hideModal}
                    existingNames={configuration.control_frameworks.map((f) => f.name)}
                    framework={displayedModal.framework}
                    onSave={(updatedFramework: DueDiligenceControlFrameworkConfiguration): void => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((f) => (f.name === displayedModal.framework.name ? updatedFramework : f)),
                            };
                        });
                        if (displayedModal.framework.name !== updatedFramework.name) {
                            updateExpandedKeysPrefix(displayedModal.framework.name, updatedFramework.name);
                        } else {
                            expandRow(updatedFramework.name);
                        }
                        hideModal();
                    }}
                />
            )}
            {displayedModal?.type === 'deleteFramework' && (
                <ConfirmationModal
                    operationType={ConfirmationModalOperationType.DELETE_WITHOUT_TYPING_TO_CONFIRM}
                    headerText="Delete Control Framework"
                    areYouSureText={`Are you sure you want to delete "${displayedModal.framework.name}"?`}
                    performOperation={async () => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.filter((f) => f.name !== displayedModal.framework.name),
                            };
                        });
                        return 'Control framework deleted.';
                    }}
                    hideModal={hideModal}
                >
                    <Text>All control groups, controls, and questions within this control framework will also be deleted.</Text>
                </ConfirmationModal>
            )}

            {displayedModal?.type === 'addGroup' && (
                <ControlGroupModal
                    hideModal={hideModal}
                    existingNames={getExistingGroupNames(displayedModal.framework)}
                    onSave={(newGroup: DueDiligenceControlGroupConfiguration): void => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) => (framework.name === displayedModal.framework.name ? { ...framework, control_groups: [...framework.control_groups, newGroup] } : framework)),
                            };
                        });
                        const groupKey = `${displayedModal.framework.name}#${newGroup.name}`;
                        expandRow(groupKey);
                        hideModal();
                    }}
                />
            )}
            {displayedModal?.type === 'editGroup' && (
                <ControlGroupModal
                    hideModal={hideModal}
                    existingNames={getExistingGroupNames(displayedModal.framework, displayedModal.group.name)}
                    group={displayedModal.group}
                    onSave={(updatedGroup: DueDiligenceControlGroupConfiguration): void => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) => (framework.name === displayedModal.framework.name ? { ...framework, control_groups: framework.control_groups.map((g) => (g.name === displayedModal.group.name ? updatedGroup : g)) } : framework)),
                            };
                        });
                        const oldGroupKey = `${displayedModal.framework.name}#${displayedModal.group.name}`;
                        const newGroupKey = `${displayedModal.framework.name}#${updatedGroup.name}`;
                        if (displayedModal.group.name !== updatedGroup.name) {
                            updateExpandedKeysPrefix(oldGroupKey, newGroupKey);
                        } else {
                            expandRow(newGroupKey);
                        }
                        hideModal();
                    }}
                />
            )}
            {displayedModal?.type === 'deleteGroup' && (
                <ConfirmationModal
                    operationType={ConfirmationModalOperationType.DELETE_WITHOUT_TYPING_TO_CONFIRM}
                    headerText="Delete Control Group"
                    areYouSureText={`Are you sure you want to delete "${displayedModal.group.name}"?`}
                    performOperation={async () => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) => (framework.name === displayedModal.framework.name ? { ...framework, control_groups: framework.control_groups.filter((g) => g.name !== displayedModal.group.name) } : framework)),
                            };
                        });
                        return 'Control group deleted.';
                    }}
                    hideModal={hideModal}
                >
                    <Text>All controls and questions within this control group will also be deleted.</Text>
                </ConfirmationModal>
            )}

            {displayedModal?.type === 'addControl' && (
                <ControlModal
                    hideModal={hideModal}
                    existingNames={getExistingControlNames(displayedModal.group)}
                    onSave={(newControl: DueDiligenceControlConfiguration): void => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) =>
                                    framework.name === displayedModal.framework.name
                                        ? {
                                              ...framework,
                                              control_groups: framework.control_groups.map((group) => (group.name === displayedModal.group.name ? { ...group, controls: [...group.controls, newControl] } : group)),
                                          }
                                        : framework
                                ),
                            };
                        });
                        const controlKey = `${displayedModal.framework.name}#${displayedModal.group.name}#${newControl.name}`;
                        expandRow(controlKey);
                        hideModal();
                    }}
                />
            )}
            {displayedModal?.type === 'editControl' && (
                <ControlModal
                    hideModal={hideModal}
                    existingNames={getExistingControlNames(displayedModal.group, displayedModal.control.name)}
                    control={displayedModal.control}
                    onSave={(updatedControl: DueDiligenceControlConfiguration): void => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) =>
                                    framework.name === displayedModal.framework.name
                                        ? {
                                              ...framework,
                                              control_groups: framework.control_groups.map((group) => (group.name === displayedModal.group.name ? { ...group, controls: group.controls.map((c) => (c.name === displayedModal.control.name ? updatedControl : c)) } : group)),
                                          }
                                        : framework
                                ),
                            };
                        });
                        const oldControlKey = `${displayedModal.framework.name}#${displayedModal.group.name}#${displayedModal.control.name}`;
                        const newControlKey = `${displayedModal.framework.name}#${displayedModal.group.name}#${updatedControl.name}`;
                        if (displayedModal.control.name !== updatedControl.name) {
                            updateExpandedKeysPrefix(oldControlKey, newControlKey);
                        } else {
                            expandRow(newControlKey);
                        }
                        hideModal();
                    }}
                />
            )}
            {displayedModal?.type === 'deleteControl' && (
                <ConfirmationModal
                    operationType={ConfirmationModalOperationType.DELETE_WITHOUT_TYPING_TO_CONFIRM}
                    headerText="Delete Control"
                    areYouSureText={`Are you sure you want to delete "${displayedModal.control.name}"?`}
                    performOperation={async () => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) =>
                                    framework.name === displayedModal.framework.name
                                        ? {
                                              ...framework,
                                              control_groups: framework.control_groups.map((group) => (group.name === displayedModal.group.name ? { ...group, controls: group.controls.filter((c) => c.name !== displayedModal.control.name) } : group)),
                                          }
                                        : framework
                                ),
                            };
                        });
                        return 'Control deleted.';
                    }}
                    hideModal={hideModal}
                >
                    <Text>All questions within this control will also be deleted.</Text>
                </ConfirmationModal>
            )}

            {displayedModal?.type === 'addQuestion' && (
                <QuestionModal
                    hideModal={hideModal}
                    module={props.module}
                    existingTexts={getExistingQuestionTexts(displayedModal.control)}
                    onSave={(newQuestion: DueDiligenceQuestionConfiguration): void => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) =>
                                    framework.name === displayedModal.framework.name
                                        ? {
                                              ...framework,
                                              control_groups: framework.control_groups.map((group) =>
                                                  group.name === displayedModal.group.name
                                                      ? {
                                                            ...group,
                                                            controls: group.controls.map((control) => (control.name === displayedModal.control.name ? { ...control, questions: [...control.questions, newQuestion] } : control)),
                                                        }
                                                      : group
                                              ),
                                          }
                                        : framework
                                ),
                            };
                        });
                        hideModal();
                    }}
                />
            )}
            {displayedModal?.type === 'editQuestion' && displayedModal.question.text && (
                <QuestionModal
                    hideModal={hideModal}
                    module={props.module}
                    existingTexts={getExistingQuestionTexts(displayedModal.control, displayedModal.question.text)}
                    question={displayedModal.question}
                    onSave={(updatedQuestion: DueDiligenceQuestionConfiguration): void => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) =>
                                    framework.name === displayedModal.framework.name
                                        ? {
                                              ...framework,
                                              control_groups: framework.control_groups.map((group) =>
                                                  group.name === displayedModal.group.name
                                                      ? {
                                                            ...group,
                                                            controls: group.controls.map((control) => (control.name === displayedModal.control.name ? { ...control, questions: control.questions.map((q) => (q.text === displayedModal.question.text ? updatedQuestion : q)) } : control)),
                                                        }
                                                      : group
                                              ),
                                          }
                                        : framework
                                ),
                            };
                        });
                        hideModal();
                    }}
                />
            )}
            {displayedModal?.type === 'deleteQuestion' && displayedModal.question.text && (
                <ConfirmationModal
                    operationType={ConfirmationModalOperationType.DELETE_WITHOUT_TYPING_TO_CONFIRM}
                    headerText="Delete Question"
                    areYouSureText={`Are you sure you want to delete "${displayedModal.question.text}"?`}
                    performOperation={async () => {
                        setConfiguration((prev) => {
                            return {
                                ...prev,
                                control_frameworks: prev.control_frameworks.map((framework) =>
                                    framework.name === displayedModal.framework.name
                                        ? {
                                              ...framework,
                                              control_groups: framework.control_groups.map((group) =>
                                                  group.name === displayedModal.group.name
                                                      ? {
                                                            ...group,
                                                            controls: group.controls.map((control) => (control.name === displayedModal.control.name ? { ...control, questions: control.questions.filter((q) => q.text !== displayedModal.question.text) } : control)),
                                                        }
                                                      : group
                                              ),
                                          }
                                        : framework
                                ),
                            };
                        });
                        return 'Question deleted.';
                    }}
                    hideModal={hideModal}
                >
                    <Text>This action cannot be undone.</Text>
                </ConfirmationModal>
            )}
        </div>
    );
};

/** Converts a question's mapped_risk_ratings array to DueDiligenceAssessmentCheckboxRowProps. */
export const questionToCheckboxes = (question: DueDiligenceQuestionConfiguration): RiskRatingCheckedStatuses => ({
    high: question.mapped_risk_ratings.includes(RiskRating.HIGH) ? 'checked' : 'unchecked',
    low: question.mapped_risk_ratings.includes(RiskRating.LOW) ? 'checked' : 'unchecked',
    lowMod: question.mapped_risk_ratings.includes(RiskRating.LOW_MODERATE) ? 'checked' : 'unchecked',
    mod: question.mapped_risk_ratings.includes(RiskRating.MODERATE) ? 'checked' : 'unchecked',
    modHigh: question.mapped_risk_ratings.includes(RiskRating.MODERATE_HIGH) ? 'checked' : 'unchecked',
});

/** Aggregates multiple checkbox states into a single state (for parent rows). */
export const aggregateCheckboxes = (children: RiskRatingCheckedStatuses[]): RiskRatingCheckedStatuses => {
    if (children.length === 0) {
        return {
            high: 'unchecked',
            low: 'unchecked',
            lowMod: 'unchecked',
            mod: 'unchecked',
            modHigh: 'unchecked',
        };
    }

    const aggregate = (statuses: CheckedStatus[]): CheckedStatus => {
        const hasChecked = statuses.some((s) => s === 'checked');
        const hasUnchecked = statuses.some((s) => s === 'unchecked');
        const hasIndeterminate = statuses.some((s) => s === 'indeterminate');

        if (hasIndeterminate || (hasChecked && hasUnchecked)) {
            return 'indeterminate';
        }
        return hasChecked ? 'checked' : 'unchecked';
    };

    return {
        high: aggregate(children.map((c) => c.high)),
        low: aggregate(children.map((c) => c.low)),
        lowMod: aggregate(children.map((c) => c.lowMod)),
        mod: aggregate(children.map((c) => c.mod)),
        modHigh: aggregate(children.map((c) => c.modHigh)),
    };
};

/** Gets the aggregated checkbox state for a control (from its questions). */
export const getControlCheckboxes = (control: DueDiligenceControlConfiguration): RiskRatingCheckedStatuses => {
    return aggregateCheckboxes(control.questions.map(questionToCheckboxes));
};

/** Gets the aggregated checkbox state for a control group (from its controls). */
export const getControlGroupCheckboxes = (group: DueDiligenceControlGroupConfiguration): RiskRatingCheckedStatuses => {
    return aggregateCheckboxes(group.controls.filter(controlHasQuestions).map(getControlCheckboxes));
};

/** Gets the aggregated checkbox state for a framework (from its control groups). */
export const getFrameworkCheckboxes = (framework: DueDiligenceControlFrameworkConfiguration): RiskRatingCheckedStatuses => {
    return aggregateCheckboxes(framework.control_groups.filter(groupHasQuestions).map(getControlGroupCheckboxes));
};

/** Gets the aggregated checkbox state for an assessment (from its frameworks). */
export const getAssessmentCheckboxes = (assessment: DueDiligenceAssessmentConfiguration): RiskRatingCheckedStatuses => {
    return aggregateCheckboxes(assessment.control_frameworks.filter(frameworkHasQuestions).map(getFrameworkCheckboxes));
};

/**
 * Sorts risk ratings (by the `RiskRating` enum's numeric order: LOW, LOW_MODERATE, MODERATE, MODERATE_HIGH, HIGH).
 * Keeping `mapped_risk_ratings` consistently ordered is required for unsaved-changes detection.
 */
export const sortRiskRatings = (riskRatings: RiskRating[]): RiskRating[] => [...riskRatings].sort((a, b) => a - b);

/** Updates a question's mapped_risk_ratings based on checkbox state, keeping them in sorted order. */
export const updateQuestionRiskRatings = (question: DueDiligenceQuestionConfiguration, checked: boolean, riskRating: RiskRating): DueDiligenceQuestionConfiguration => ({
    ...question,
    mapped_risk_ratings: checked ? sortRiskRatings([...new Set([...question.mapped_risk_ratings, riskRating])]) : question.mapped_risk_ratings.filter((r) => r !== riskRating),
});

/** Returns a copy of the configuration with every question's `mapped_risk_ratings` sorted. */
export const normalizeRiskRatingOrder = (configuration: DueDiligenceAssessmentConfiguration): DueDiligenceAssessmentConfiguration => ({
    ...configuration,
    control_frameworks: configuration.control_frameworks.map((framework) => ({
        ...framework,
        control_groups: framework.control_groups.map((group) => ({
            ...group,
            controls: group.controls.map((control) => ({
                ...control,
                questions: control.questions.map((question) => ({ ...question, mapped_risk_ratings: sortRiskRatings(question.mapped_risk_ratings) })),
            })),
        })),
    })),
});

/** Checks if a control has any questions. */
export const controlHasQuestions = (control: DueDiligenceControlConfiguration): boolean => {
    return control.questions.length > 0;
};

/** Checks if a control group has any questions (in any of its controls). */
export const groupHasQuestions = (group: DueDiligenceControlGroupConfiguration): boolean => {
    return group.controls.some(controlHasQuestions);
};

/** Checks if a framework has any questions (in any of its control groups). */
export const frameworkHasQuestions = (framework: DueDiligenceControlFrameworkConfiguration): boolean => {
    return framework.control_groups.some(groupHasQuestions);
};
